import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';
import type { MultiSelectOption } from '@/ui/base/multi-select-combobox';

import type { Dre } from './contrato';

/**
 * O que as cinco listagens do credenciamento e da frota buscam em comum.
 *
 * O desenho e o mesmo do conector gov.br, que foi a primeira tela do sistema a
 * buscar dados (modulos/admin/integracoes/dados.ts): NENHUM COMPONENTE CHAMA
 * `chamarApi` DIRETO, e as chaves de cache moram junto de quem as invalida. A tela
 * pede um gancho e recebe estado; caminho, metodo e chave sao assunto deste arquivo.
 *
 * O FRONT NAO FALA COM O BANCO. Estas telas vieram de um protótipo que consultava o
 * banco direto do navegador; aqui a leitura passa pela API NestJS, que aplica guarda
 * de papel e recorte por diretoria antes de devolver linha
 * (docs/ARQUITETURA.md, secao 1). O ESLint recusa o atalho: importar cliente de banco
 * em `src/` e erro de lint, nao questao de estilo.
 */

export const CHAVES_DA_OPERACAO = {
  dres: ['operacao', 'dres'] as const,
  recursosAtivos: ['operacao', 'recursos-ativos-em-os'] as const,
};

/**
 * 401 e 403 nao se resolvem tentando de novo.
 *
 * O 401 pede entrar de novo — e o cliente da API ja renovou o token por conta antes
 * de desistir. O 403 pede outro caminho: a resposta seria identica na segunda vez, e
 * o unico efeito de insistir seria dobrar a espera antes de a tela dizer o que houve.
 *
 * Vive aqui e nao em cada arquivo de dados para nao virar cinco copias que divergem.
 */
export function naoInsistirEmRecusa(tentativas: number, erro: unknown): boolean {
  if (erro instanceof ErroApi && (erro.pedeEntrarDeNovo || erro.pedeOutroCaminho)) {
    return false;
  }
  return tentativas < 1;
}

/**
 * As diretorias regionais de ensino, de `public.dres` pela API
 * (`GET /bff/v1/operacao/dres`, modulo `credenciados-operacao`). Qualquer papel da
 * operacao le a lista inteira: e vocabulario do filtro. O recorte por DRE e do DADO
 * filtrado, e quem o aplica e o banco — escolher outra diretoria no combo nao abre
 * nada para quem e de DRE.
 *
 * Cache longo de proposito: a lista muda de ano em ano, e ela e usada em filtro —
 * rebuscar a cada troca de pagina seria uma requisicao por nada.
 */
export function useDres(): UseQueryResult<Dre[], ErroApi> {
  return useQuery<Dre[], ErroApi>({
    queryKey: CHAVES_DA_OPERACAO.dres,
    queryFn: ({ signal }) => chamarApi<Dre[]>('/bff/v1/operacao/dres', { sinal: signal }),
    staleTime: 10 * 60_000,
    retry: naoInsistirEmRecusa,
  });
}

/**
 * As diretorias no formato que o `MultiSelectCombobox` espera.
 *
 * Devolve lista vazia enquanto carrega, se a busca falhar OU se a resposta nao for uma
 * lista — o filtro aparece sem opcao em vez de a tela inteira cair por causa de um seletor
 * secundario.
 *
 * `Array.isArray` NAO E PARANOIA: `data ?? []` cobre nulo e indefinido, e nao cobre a
 * resposta que chega com forma diferente da esperada — envelope de paginacao onde se
 * esperava lista crua, ou corpo de erro devolvido com 200. Nos dois casos `.map` estoura, e
 * o estouro acontece durante o render de uma listagem inteira por causa de um combo de
 * filtro. Foi exatamente isso que apareceu ao exercitar a tela.
 */
export function useOpcoesDeDre(): MultiSelectOption[] {
  const { data } = useDres();
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((dre) => ({ value: dre.id, label: dre.nome, code: dre.codigo }));
}

/** Identificadores de recurso em ordem de servico em execucao, como a API devolve. */
interface RecursosAtivosResposta {
  condutores: string[];
  monitores: string[];
  veiculos: string[];
}

export interface RecursosAtivos {
  condutores: Set<string>;
  monitores: Set<string>;
  veiculos: Set<string>;
}

/**
 * Quem esta vinculado a uma ordem de servico EM EXECUCAO.
 *
 * E o que sustenta o filtro "Ativo em OS" nas listas de condutores, monitores e
 * veiculos: sem ele, a operacao nao consegue separar o cadastro inteiro de quem esta
 * de fato rodando hoje.
 *
 * `Set` e nao lista porque o uso e sempre "este id esta dentro?", uma vez por linha
 * da tabela. Com lista, cada pagina de 50 linhas faria 50 varreduras completas.
 *
 * ORDENS DE SERVICO SAO DA FASE 4 (docs/PLANO-DE-FASES.md). Enquanto a rota nao
 * existir, esta consulta falha e o filtro simplesmente nao recorta nada — a lista
 * aparece inteira, que e o comportamento seguro. O que NAO se faz e esconder o filtro
 * fingindo que a pergunta nao existe, nem inventar resposta.
 */
export function useRecursosAtivosEmOs(habilitado = true): UseQueryResult<RecursosAtivos, ErroApi> {
  return useQuery<RecursosAtivos, ErroApi>({
    queryKey: CHAVES_DA_OPERACAO.recursosAtivos,
    queryFn: async ({ signal }) => {
      const resposta = await chamarApi<RecursosAtivosResposta>(
        '/ordens-de-servico/recursos-ativos',
        { sinal: signal },
      );
      // Mesma cautela de `useOpcoesDeDre`: `new Set` de algo que nao e iteravel estoura.
      const comoConjunto = (valores: unknown) =>
        new Set(Array.isArray(valores) ? (valores as string[]) : []);
      return {
        condutores: comoConjunto(resposta?.condutores),
        monitores: comoConjunto(resposta?.monitores),
        veiculos: comoConjunto(resposta?.veiculos),
      };
    },
    enabled: habilitado,
    staleTime: 5 * 60_000,
    retry: naoInsistirEmRecusa,
  });
}
